/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { EssTableCustomElement } from 'app/shared/elements/ess-table';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { CaseService } from 'app/cm/services/case-service';
import * as CM from 'app/cm/models/essette-api';

export interface CustomCareTeamParticipant extends CM.CareTeamParticipant {
    displayName?: string | null;
}

@autoinject()
export class CareTeamGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected householdCaseId: string | null = null;

    protected careTeamParticipants: ReadonlyArray<CustomCareTeamParticipant> = [];

    protected showMemberSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;

    private sorting: Sorting = new Sorting();
    private defaultSorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 25;


    ////****************** Element References ***************************************////
    protected $table!: EssTableCustomElement<CustomCareTeamParticipant[]>;
    protected $pager!: EssPagerCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private service: CaseService,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();

        // Default Sort

        this.defaultSorting.entries = [
            {
                reference: 'case.member.description',
                direction: SortDirection.Ascending
            }
        ];
    }

    protected bind(): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.reload();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadHouseholdCaseCareTeam();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    public async loadHouseholdCaseCareTeam(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            includeInactive: false,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting.entries.length ? this.sorting : this.defaultSorting
        };

        const includes = [
            'assigned.toProvider',
            'assigned.toUser',
            'case',
            'case.assignedTo',
            'case.member',
            'metadata.assigned.byUser',
            'relationship'
        ];

        const fields = [
            'assigned.toUser.name.full',
            'assigned.toProvider.name.full',
            'relationship.description',
            'case.member.description',
            'case.member.name.full',
            'case.number',
            'metadata.assigned.byUser.description',
            'metadata.assigned.timestamp'
        ];

        try {
            this.state.beginLoad();

            const response = await this.service.loadHouseholdCaseCareTeam(this.householdCaseId, options, includes, fields);
            this.careTeamParticipants = response.results;
            // Set up team member name and display names
            this.careTeamParticipants.forEach(participant => {
                let userLoginName = participant?.assigned?.toUser?.name?.full;
                if(!!participant?.assigned?.toUser?.login?.loginName) userLoginName += ` (${participant.assigned.toUser.login.loginName})`;
                participant.displayName = userLoginName || participant?.assigned?.toProvider?.name?.full;
            });

            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
